"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

// Fungsi Helper untuk Grouping Data Rekap by Fakultas
const processFacultyData = (rekap: any[]) => {
  if (!rekap) return [];

  const grouped = rekap.reduce((acc: any, curr: any) => {
    const key = curr.fakultas_nama;
    if (!acc[key]) {
      acc[key] = {
        fakultas: key,
        pendaftar: 0,
        daya_tampung: 0,
        lulus: 0,
      };
    }
    acc[key].pendaftar += curr.jumlah_pendaftar;
    acc[key].daya_tampung += curr.daya_tampung;
    acc[key].lulus += curr.jumlah_lulus;
    return acc;
  }, {});

  // Ubah ke array dan sort berdasarkan pendaftar terbanyak
  return Object.values(grouped)
    .sort((a: any, b: any) => b.pendaftar - a.pendaftar)
    .slice(0, 5); // Ambil Top 5
};

const chartConfig = {
  pendaftar: { label: "Pendaftar", color: "hsl(var(--chart-1))" },
  daya_tampung: { label: "Daya Tampung", color: "hsl(var(--chart-2))" },
} satisfies ChartConfig;

export function FacultyInterestChart({
  data,
  isLoading,
}: {
  data: any;
  isLoading: boolean;
}) {
  const chartData = processFacultyData(data?.rekap || []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top 5 Fakultas Paling Diminati</CardTitle>
        <CardDescription>
          Perbandingan Jumlah Pendaftar vs Daya Tampung
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex flex-col gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-full rounded-md" />
              </div>
            ))}
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="min-h-[300px] w-full">
            <BarChart
              accessibilityLayer
              data={chartData}
              layout="vertical" // Mode Horizontal
              margin={{ left: 20 }}
            >
              <CartesianGrid horizontal={false} />
              <YAxis
                dataKey="fakultas"
                type="category"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                width={150} // Beri ruang untuk nama fakultas
                tickFormatter={(value) =>
                  value.length > 20 ? `${value.slice(0, 20)}...` : value
                }
              />
              <XAxis dataKey="pendaftar" type="number" hide />
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar
                dataKey="pendaftar"
                fill="var(--color-pendaftar)"
                radius={[0, 4, 4, 0]}
                barSize={20}
              />
              <Bar
                dataKey="daya_tampung"
                fill="var(--color-daya_tampung)"
                radius={[0, 4, 4, 0]}
                barSize={20}
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
